import { useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { LOGIN_HERO_IMAGE } from '../../../../../config/env';
import {
    useGetQueueRevenue30dayQuery,
    useLazyGetMonthlyStatsTableQuery,
} from '../../../../../store/excecutive/executiveApi';

const HOTEL_SUB = '921544e4-20c1-703b-c1d4-62f60c91cf49';
const POLL_MS = 7000

const formatMoney = (value: unknown) => {
    const n = Number(value);
    const safe = Number.isFinite(n) ? n : 0;
    return `$${safe.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const formatNumber2 = (value: unknown) => {
    const n = Number(value);
    const safe = Number.isFinite(n) ? n : 0;
    return safe.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const formatPercent2 = (value: unknown) => {
    const n = Number(value);
    const safe = Number.isFinite(n) ? n : 0;
    return `${safe.toFixed(2)}%`;
};

const formatInt = (value: unknown) => {
    const n = Number(value);
    const safe = Number.isFinite(n) ? n : 0;
    return Math.round(safe).toLocaleString();
};

const InfoTooltip = ({
    label,
    description,
}: {
    label: string;
    description: string;
}) => (
    <div className="relative group">
        <button
            type="button"
            aria-label={`${label} definition`}
            className="ml-1 mb-1 h-4 w-4 flex items-center justify-center">
            <img src="/svg/info-3.png" alt="" className="h-4 w-4 object-contain" />
        </button>
        <div className="pointer-events-none absolute left-0 top-6 z-20 w-56 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0f172a] p-2 text-xs text-gray-700 dark:text-gray-200 shadow-lg opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
            {description.split(/\\n|\r?\n/).map((line, idx, arr) => (
                <span key={`${label}-${idx}`}>
                    {line}
                    {idx < arr.length - 1 ? (
                        <br />
                    ) : null}
                </span>
            ))}
        </div>
    </div>
);

export default function MonthlyStats() {
    // Extra guard so this never shows for other tenants.
    if (LOGIN_HERO_IMAGE !== '/images/claire-glen-ivy-login.jpeg') return null;

    const { data } = useGetQueueRevenue30dayQuery(
        { hotelSub: HOTEL_SUB },
        { pollingInterval: POLL_MS, refetchOnMountOrArgChange: true },
    );

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [downloadMonthlyStats, { isLoading: isDownloading }] =
        useLazyGetMonthlyStatsTableQuery();

    const reportOptions = useMemo(
        () =>
            [
                {
                    label: 'Calendar Month-to-Date',
                    value: 'calendar_mtd' as const,
                },
                {
                    label: 'Last Full Calendar Month',
                    value: 'last_full_calendar_month' as const,
                },
            ] as const,
        [],
    );

    const buildFileName = (reportPeriod: string, payload: any) => {
        const start = payload?.period?.startDate
            ? String(payload.period.startDate).slice(0, 10)
            : 'start';
        const end = payload?.period?.endDate
            ? String(payload.period.endDate).slice(0, 10)
            : 'end';
        return `monthly-stats-${reportPeriod}-${start}-to-${end}.xlsx`;
    };

    const downloadReport = async (
        reportPeriod: (typeof reportOptions)[number]['value'],
    ) => {
        try {
            const payload = await downloadMonthlyStats(
                { hotelSub: HOTEL_SUB, reportPeriod },
                true,
            ).unwrap();

            const row = Array.isArray(payload?.rows) ? payload.rows[0] : undefined;

            const getNum = (value: unknown) => {
                const n = Number(value);
                return Number.isFinite(n) ? n : 0;
            };

            const allZeroForLastFullMonth =
                reportPeriod === 'last_full_calendar_month' &&
                ['hoursWorked', 'queueTimeHours', 'callsTaken', 'abandonedCalls', 'totalRevenue'].every(
                    (key) => getNum((row as any)?.[key]) === 0,
                );

            if (allZeroForLastFullMonth) {
                setErrorMessage(
                    'This report can be run when a full calendar month of data is available. In the meantime please select the month to date option.',
                );
                return;
            }

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet(
                reportPeriod === 'calendar_mtd'
                    ? 'Calendar MTD'
                    : 'Last Full Month',
            );

            worksheet.columns = [
                { header: 'Employee', key: 'employee', width: 26 },
                { header: 'Hours Worked', key: 'hoursWorked', width: 14 },
                { header: 'Queue Time', key: 'queueTimeHours', width: 12 },
                { header: 'Hours/Queue', key: 'hoursQueuePercent', width: 16 },
                { header: 'Calls Taken', key: 'callsTaken', width: 12 },
                { header: 'Missed Calls', key: 'abandonedCalls', width: 14 },
                { header: 'Total Revenue', key: 'totalRevenue', width: 14 },
                { header: 'Revenue/Hour', key: 'revenuePerHourUsd', width: 14 },
            ];

            const headerRow = worksheet.getRow(1);
            headerRow.height = 22;
            headerRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: 'FF000000' } };
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE1B8E7' }, // light purple like screenshot
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF000000' } },
                    left: { style: 'thin', color: { argb: 'FF000000' } },
                    bottom: { style: 'thin', color: { argb: 'FF000000' } },
                    right: { style: 'thin', color: { argb: 'FF000000' } },
                };
            });

            const dataExcelRow = worksheet.addRow({
                employee: row?.employee ?? '',
                hoursWorked: row?.hoursWorked ?? null,
                queueTimeHours: row?.queueTimeHours ?? null,
                hoursQueuePercent:
                    row?.hoursQueuePercent != null ? Number(row.hoursQueuePercent) / 100 : null,
                callsTaken: row?.callsTaken ?? null,
                abandonedCalls: row?.abandonedCalls ?? null,
                totalRevenue: row?.totalRevenue ?? null,
                revenuePerHourUsd: row?.revenuePerHourUsd ?? null,
            });

            dataExcelRow.height = 20;
            dataExcelRow.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF000000' } },
                    left: { style: 'thin', color: { argb: 'FF000000' } },
                    bottom: { style: 'thin', color: { argb: 'FF000000' } },
                    right: { style: 'thin', color: { argb: 'FF000000' } },
                };
                if (colNumber === 1) {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
            });

            // Number formats
            worksheet.getCell('B2').numFmt = '0.00';
            worksheet.getCell('C2').numFmt = '0.00';
            worksheet.getCell('D2').numFmt = '0%';
            worksheet.getCell('E2').numFmt = '0';
            worksheet.getCell('F2').numFmt = '0';
            worksheet.getCell('G2').numFmt = '$#,##0';
            worksheet.getCell('H2').numFmt = '$#,##0.00';

            // Freeze header row
            worksheet.views = [{ state: 'frozen', ySplit: 1 }];

            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(
                new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }),
                buildFileName(reportPeriod, payload),
            );
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert('Failed to download report. Please try again.');
        } finally {
            setIsMenuOpen(false);
        }
    };

    return (
                <div className="mt-8 mb-8">
            {errorMessage ? (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="w-full max-w-md rounded-2xl bg-white dark:bg-[#121826] p-6 border border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                            {errorMessage}
                        </p>
                        <div className="mt-5 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setErrorMessage(null)}
                                className="bg-[#00330F] text-white rounded-lg px-4 py-2 text-sm min-w-44">
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            <div className="mt-2 mb-3 flex flex-col md:flex-row flex-wrap md:items-center">
                <h4
                    className="mb-1 text-[20px] leading-[20px] font-semibold tracking-[0px] text-[#101010] dark:text-white"
                    style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                    Monthly Stats
                </h4>
                <div className="md:ml-auto flex items-center gap-3 no-print">
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setIsMenuOpen((v) => !v)}
                            disabled={isDownloading}
                            className="h-[36px] px-4 rounded-full bg-[#00330F] text-white text-sm font-medium disabled:opacity-60"
                            style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                            {isDownloading ? 'Downloading…' : 'Download Report'}
                        </button>
                        {isMenuOpen ? (
                            <div className="absolute right-0 mt-2 w-64 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0f172a] shadow-lg overflow-hidden z-30">
                                {reportOptions.map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => downloadReport(opt.value)}
                                        className="w-full text-left px-4 py-3 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-white/5"
                                        style={{
                                            fontFamily: 'Inter, Arial, sans-serif',
                                        }}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[12px]">
                <div className="w-full">
                    <div className="relative h-[104px] w-full rounded-[14px] border border-[#00330F] px-5 py-4 flex flex-col justify-between gap-[6px] overflow-visible dark:bg-[#1F1F1F]">
                        <div className="flex items-center gap-1">
                            <p
                                className="text-sm font-semibold leading-[1.2] tracking-[0px] text-[#101010] dark:text-white"
                                style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                                Revenue / Hour
                            </p>
                            <InfoTooltip
                                label="Revenue / Hour"
                                description="Average booking revenue generated per hour of call time over the last 30 days.\nThis value is calculated accurate to the second based on Claire call time (working hours)."
                            />
                        </div>
                        <p
                            className="mb-1 text-[1.5rem] leading-[1.2] font-medium tracking-[0px] text-[#00330F] dark:text-white"
                            style={{
                                fontFamily:
                                    '"Plus Jakarta Sans", Inter, Arial, sans-serif',
                            }}>
                            {formatMoney(data?.bookingValuePerHourUsd)}
                        </p>
                    </div>
                </div>

                <div className="w-full">
                    <div className="relative h-[104px] w-full rounded-[14px] border border-[#00330F] px-5 py-4 flex flex-col justify-between gap-[6px] overflow-visible dark:bg-[#1F1F1F]">
                        <div className="flex items-center gap-1">
                            <p
                                className="text-sm font-semibold leading-[1.2] tracking-[0px] text-[#101010] dark:text-white"
                                style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                                Queue Time (min)
                            </p>
                            <InfoTooltip
                                label="Queue Time (min)"
                                description="Estimated total customer wait time, shown in minutes, over the last 30 days."
                            />
                        </div>
                        <p
                            className="mb-1 text-[1.5rem] leading-[1.2] font-medium tracking-[0px] text-[#00330F] dark:text-white"
                            style={{
                                fontFamily:
                                    '"Plus Jakarta Sans", Inter, Arial, sans-serif',
                            }}>
                            {formatNumber2(data?.queueTimeMinutes)}
                        </p>
                    </div>
                </div>

                <div className="w-full">
                    <div className="relative h-[104px] w-full rounded-[14px] border border-[#00330F] px-5 py-4 flex flex-col justify-between gap-[6px] overflow-visible dark:bg-[#1F1F1F]">
                        <div className="flex items-center gap-1">
                            <p
                                className="text-sm font-semibold leading-[1.2] tracking-[0px] text-[#101010] dark:text-white"
                                style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                                Hours/Queue
                            </p>
                            <InfoTooltip
                                label="Hours/Queue"
                                description="The share of time spent waiting in the queue compared to time spent on calls over the last 30 days."
                            />
                        </div>
                        <p
                            className="mb-1 text-[1.5rem] leading-[1.2] font-medium tracking-[0px] text-[#00330F] dark:text-white"
                            style={{
                                fontFamily:
                                    '"Plus Jakarta Sans", Inter, Arial, sans-serif',
                            }}>
                            {formatPercent2(data?.hoursQueuePercent)}
                        </p>
                    </div>
                </div>

                <div className="w-full">
                    <div className="relative h-[104px] w-full rounded-[14px] border border-[#00330F] px-5 py-4 flex flex-col justify-between gap-[6px] overflow-visible dark:bg-[#1F1F1F]">
                        <div className="flex items-center gap-1">
                            <p
                                className="text-sm font-semibold leading-[1.2] tracking-[0px] text-[#101010] dark:text-white"
                                style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                                Missed Calls
                            </p>
                            <InfoTooltip
                                label="Missed Calls"
                                description="Number of time caller hung up before Claire answered."
                            />
                        </div>
                        <p
                            className="mb-1 text-[1.5rem] leading-[1.2] font-medium tracking-[0px] text-[#00330F] dark:text-white"
                            style={{
                                fontFamily:
                                    '"Plus Jakarta Sans", Inter, Arial, sans-serif',
                            }}>
                            {formatInt(data?.abandonedCalls)}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

